import type { ItineraryLeg, SegmentMode, TransferKind, TravelMode } from '../api/directions.api';
import { decodeEncodedPolyline } from './polyline';
import { formatMinutes } from './duration';

/** One drawable piece of a leg. A ferry leg has three; everything else has one. */
export interface ClientSegment {
  mode:          SegmentMode;
  durationSec:   number;
  distanceM:     number;
  decodedPath:   google.maps.LatLng[];
  instructions?: string | null;
}

// Backend-computed leg with its polylines pre-decoded for cheap rendering and
// for reading start/end positions when activities lack venue coords.
export interface ClientLeg {
  durationSec: number;
  distanceM:   number;
  decodedPath: google.maps.LatLng[];
  mode:        TravelMode;
  /** What the server asked Google for. Differs from `mode` when that had no
      route here — which is how the UI knows to grey that mode out. */
  requestedMode: TravelMode;
  segments:      ClientSegment[];
}

export const LEG_MODE_COLOR: Record<string, string> = {
  WALKING:   '#0d9488',
  DRIVING:   '#6366f1',
  BICYCLING: '#f59e0b',
  TRANSIT:   '#ec4899',
  FERRY:     '#0ea5e9',
};

/** Server leg → drawable leg: decode each segment once, keep the whole path too. */
export function toClientLeg(leg: ItineraryLeg): ClientLeg {
  const segments: ClientSegment[] = leg.segments.map(s => ({
    mode:         s.mode,
    durationSec:  s.durationSec,
    distanceM:    s.distanceM,
    instructions: s.instructions,
    decodedPath:  decodeEncodedPolyline(s.polyline),
  }));
  return {
    durationSec: leg.durationSec,
    distanceM:   leg.distanceM,
    decodedPath: segments.flatMap(s => s.decodedPath),
    mode:          leg.mode,
    requestedMode: leg.requestedMode,
    segments,
  };
}

export function formatLegDuration(secs: number): string {
  return formatMinutes(Math.round(secs / 60));
}

/** ADR-0031: the transfer a leg is, read from its one segment; null for a routed leg. */
export function transferKindOf(leg: { segments?: readonly { mode: SegmentMode }[] } | null | undefined): TransferKind | null {
  const mode = leg?.segments?.[0]?.mode;
  return mode === 'FLIGHT' || mode === 'TRANSFER' ? mode : null;
}
